/*
 * Glass-tube thermometer. The slider's gradient track (blue → amber →
 * red) reads as the liquid; the wrapping .tube + gloss overlay + glossy
 * thumb make it feel like a clear horizontal thermometer rather than a
 * plain HTML range input.
 *
 * Slider remains an <input type="range"> so keyboard nav + touch
 * dragging keep working — the dressing is pure CSS.
 */

.thermometer {
  margin-top: var(--token-space-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--token-space-md);
}

.label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  font-family: var(--token-font-ui);
  width: 100%;
  max-width: 720px;
}

/* Matches the Weather page's "How is the weather today?" headline so
 * the two surfaces read at the same scale + family. Drops the small-
 * uppercase eyebrow treatment in favor of the title voice. */
.labelText {
  font-family: var(--token-font-body);
  font-size: clamp(28px, 3vw, 56px);
  letter-spacing: 0.3px;
  color: var(--token-text-primary);
}

/* The "glass tube" wrapping the slider. Provides the rounded shell +
 * drop shadow + a frosted gloss overlay. Vertical padding gives the
 * air-bubble thumb room to render above + below the gradient track
 * without being clipped. */
.tube {
  position: relative;
  width: 100%;
  padding: 12px 0;
  border-radius: 999px;
  /* Layered shadows: outer soft drop, tight inner shadow for the
   * "glass edge" curvature, and a bright top-inside highlight so the
   * tube reads as a real curved-glass surface. */
  box-shadow:
    0 14px 28px -10px rgba(8, 18, 12, 0.4),
    0 2px 6px rgba(8, 18, 12, 0.12),
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.7),
    inset 0 4px 10px rgba(255, 255, 255, 0.55),
    inset 0 -3px 8px rgba(8, 18, 12, 0.18);
  /* Very faintly tinted glass — almost clear, with just enough body
   * for the inner highlights to register. */
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px) saturate(115%);
  -webkit-backdrop-filter: blur(4px) saturate(115%);
}

.slider {
  display: block;
  width: 100%;
  /* Taller bar — was 56, now 72 — so the thumb sits comfortably
   * inside the tube's content area + padding. */
  height: 72px;
  appearance: none;
  -webkit-appearance: none;
  /* On a touchscreen (Promethean board / iPad) the browser otherwise
   * treats a horizontal drag on a range input as a candidate pan
   * gesture, swallowing the first ~10px before the thumb starts to
   * track. `none` hands every pointer event to the slider so drag is
   * instant. Live mode has no page scroll to disable. */
  touch-action: none;
  /* Multi-layer track: the colored gradient is the bottom layer (the
   * "liquid"), then a thin upper-stripe highlight + lower-stripe
   * shadow paint the glass-curvature on top. */
  background:
    linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0.18) 22%,
      rgba(255, 255, 255, 0) 50%,
      rgba(8, 18, 12, 0.18) 100%
    ),
    linear-gradient(to right, #4a90ff 0%, #f5b94c 60%, #e74c3c 100%);
  border-radius: 999px;
  outline: none;
  margin: 0;
  cursor: pointer;
}

.slider:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--token-accent) 65%, transparent);
  outline-offset: 4px;
}

/* Temperature value drawn on top of the slider bubble. The bubble's
 * horizontal position comes from the --thumb-progress CSS variable
 * set on .tube — pure CSS, no JS required to track drag movement.
 * The thumb is 76px wide; the label sits at the visual centre by
 * mapping progress 0%..100% onto the available track range
 * (track − thumb), then offsetting by half-thumb. */
.thumbLabel {
  position: absolute;
  top: 50%;
  left: calc(38px + var(--thumb-progress, 0%) * (100% - 76px) / 100%);
  transform: translate(-50%, -50%);
  pointer-events: none;
  font-family: var(--token-font-ui);
  font-weight: 700;
  font-size: 22px;
  color: rgba(28, 38, 32, 0.88);
  letter-spacing: 0.2px;
  /* Subtle white halo so the value reads against the bubble's mixed
   * highlight gradient. */
  text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85),
    0 0 8px rgba(255, 255, 255, 0.6);
  user-select: none;
  z-index: 2;
}

/* Soft outer gloss across the full tube — sits on top of the slider
 * so the curvature reads even where the thumb has slid past.
 * pointer-events: none keeps drag working underneath. */
.gloss {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  background:
    /* Top sheen — bright at the top, fades to nothing by mid-height */
    linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.45) 0%,
      rgba(255, 255, 255, 0.12) 30%,
      rgba(255, 255, 255, 0) 55%
    ),
    /* Subtle end-cap rounded shadows on the left + right edges */
    radial-gradient(ellipse at 4% 50%, rgba(8, 18, 12, 0.18) 0%, transparent 18%),
    radial-gradient(ellipse at 96% 50%, rgba(8, 18, 12, 0.18) 0%, transparent 18%);
}

/* Air-bubble thumb. Mostly translucent with a bright highlight at the
 * upper-left so it reads as a hollow glass bubble rather than a solid
 * bead. The gradient curve runs: bright spot → mid-translucent body →
 * a brighter rim on the bottom edge for the "glass meniscus" look. */
.slider::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 28%,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.55) 16%,
    rgba(255, 255, 255, 0.18) 44%,
    rgba(255, 255, 255, 0.32) 72%,
    rgba(255, 255, 255, 0.7) 96%
  );
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  /* Soft drop + an inner ring for the bubble's spherical curvature */
  box-shadow:
    0 8px 16px -6px rgba(8, 18, 12, 0.45),
    0 2px 4px rgba(8, 18, 12, 0.18),
    inset 0 -6px 12px rgba(255, 255, 255, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.55),
    inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  cursor: grab;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;
}

.slider::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.04);
}

.slider::-moz-range-thumb {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 28%,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.55) 16%,
    rgba(255, 255, 255, 0.18) 44%,
    rgba(255, 255, 255, 0.32) 72%,
    rgba(255, 255, 255, 0.7) 96%
  );
  border: 1.5px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 8px 16px -6px rgba(8, 18, 12, 0.45),
    0 2px 4px rgba(8, 18, 12, 0.18),
    inset 0 -6px 12px rgba(255, 255, 255, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.55),
    inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  cursor: grab;
}

/* Matches the "Temperature" heading size + family so the user-set
 * value reads as a peer of the heading, not a separate display piece. */
.readout {
  font-family: var(--token-font-display);
  /* −1 type-scale step (÷1.2) from clamp(28px, 3vw, 56px). */
  font-size: clamp(23px, 2.5vw, 47px);
  color: var(--token-text-primary);
  letter-spacing: 0.3px;
  line-height: 1;
  /* Frosted-glass pill so the value reads as a callout, not body
   * copy. Matches the thermometer tube's glass treatment above:
   * fully-rounded ends, very translucent fill, soft blur, layered
   * shadows for the "curved glass" edge feel. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: clamp(10px, 1vw, 16px) clamp(20px, 2vw, 32px);
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px) saturate(115%);
  -webkit-backdrop-filter: blur(4px) saturate(115%);
  border-radius: 999px;
  box-shadow:
    0 14px 28px -10px rgba(8, 18, 12, 0.4),
    0 2px 6px rgba(8, 18, 12, 0.12),
    inset 0 0 0 1.5px rgba(255, 255, 255, 0.7),
    inset 0 4px 10px rgba(255, 255, 255, 0.55),
    inset 0 -3px 8px rgba(8, 18, 12, 0.18);
}

.scale {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 720px;
  font-family: var(--token-font-ui);
  font-size: 12px;
  opacity: 0.7;
}

/* Descriptive-word scale — four labels evenly spaced under the tube
 * so kids can see where on the gauge each word lives. */
.wordScale {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  max-width: 720px;
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.65);
  text-align: center;
}

.tick {
  font-family: var(--token-font-ui);
}
